import {
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
  LOCALE_ID,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import localeDe from '@angular/common/locales/de';
import { provideRouter } from '@angular/router';
import { IDBPDatabaseService } from './services/IDBPDatabase.service';

import { routes } from './app.routes';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from '../environments/environment';
import { BASE_PATH } from './openapi';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { AuthenticationService } from './core/services/authentication.service';
import { noAccessInterceptor } from './core/interceptors/no-access.interceptor';
import { idempotencyInterceptor } from './core/interceptors/idempotency.interceptor';
import { registerLocaleData } from '@angular/common';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { calculationReducer } from './store/calculation/calculation.reducer';
import { CalculationEffects } from './store/calculation/calculation.effects';

import { setCulture, loadCldr } from '@syncfusion/ej2-base';

// CLDR JSON imports for number/date formatting
import * as numberingSystems from 'cldr-data/supplemental/numberingSystems.json';
import * as deNumbers from 'cldr-data/main/de/numbers.json';
import * as deCaGregorian from 'cldr-data/main/de/ca-gregorian.json';
import * as deTimeZoneNames from 'cldr-data/main/de/timeZoneNames.json';
import * as weekData from 'cldr-data/supplemental/weekData.json';
import { APP_CONFIG } from './core/config/app-config';
import { FixedContentsParamsComponent } from './features/calculations/calculation/features/reports/report-params/fixed-contents-params/fixed-contents-params.component';
import { REPORT_PARAMS } from './shared/tokens/report-params';
import { ErectionSupervisionAndStartupParamsComponent } from './features/calculations/calculation/features/reports/report-params/erection-supervision-and-startup-params/erection-supervision-and-startup-params.component';

// Only this: load CLDR data and set culture to 'de'
loadCldr(numberingSystems, deNumbers, deCaGregorian, deTimeZoneNames, weekData);

setCulture('de'); // ✅ enables comma for decimal, dot for thousand separator

registerLocaleData(localeDe);

const httpLoaderFactory: (http: HttpClient) => TranslateHttpLoader = (http: HttpClient) =>
  new TranslateHttpLoader(http, './i18n/');

export function loggerCallback(logLevel: LogLevel, message: string) {
  if (logLevel === LogLevel.Error || logLevel === LogLevel.Warning) {
    console.warn(message);
  }
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: '/',
      postLogoutRedirectUri: '/',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string> | null>();
  protectedResourceMap.set(environment.apiConfig.uri, environment.apiConfig.scopes);
  protectedResourceMap.set(environment.apiADConfig.uri, environment.apiADConfig.scopes);
  protectedResourceMap.set(environment.api, environment.apiConfig.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
    },
    loginFailedRoute: '/login-failed',
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: APP_CONFIG,
      useValue: {
        appVersion: 'v0.0.40',
        dateTimeFormat: 'dd.MM.yyyy; HH:mm',
        spOwnScopeIdentifier: 'SP own Scope',
      },
    },
    {
      provide: REPORT_PARAMS,
      useValue: [
        {
          reportTemplateId: 'detailed-calculation-overview-fixed-contents',
          component: FixedContentsParamsComponent,
        },
        {
          reportTemplateId: 'erection-supervision-startup',
          component: ErectionSupervisionAndStartupParamsComponent,
        },
      ],
    },
    provideStore({
      calculation: calculationReducer,
    }),
    provideStoreDevtools({
      maxAge: 25, // Optional: number of states to retain
      logOnly: environment.production, // Restrict extension in production
    }),
    provideEffects([CalculationEffects]),
    provideAnimations(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    { provide: BASE_PATH, useValue: environment.api },
    { provide: LOCALE_ID, useValue: 'de-DE' },
    importProvidersFrom([
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: httpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ]),
    provideHttpClient(
      withInterceptors([noAccessInterceptor, errorInterceptor, idempotencyInterceptor]),
      withInterceptorsFromDi()
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    provideAppInitializer(() => {
      const db = inject(IDBPDatabaseService);
      return db.initialize();
    }),
    // keeps the splash screen up until MSAL is ready and the application user is loaded (or failed to load)
    provideAppInitializer(() => {
      const authenticationService = inject(AuthenticationService);
      return authenticationService.initializeAuthentication();
    }),
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
};
